import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, ChevronRight, Instagram, ArrowRight, Ticket } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { PlanetLogo } from "@/components/PlanetLogo";
import { NAV_LINKS, FOOTER, TICKETS } from "@/content/galaxy";
import { cn } from "@/lib/utils";

const INSTAGRAM = FOOTER.socials.find((s) => s.icon === "instagram")?.href;

export const SiteNavbar = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={cn(
        "fixed left-0 right-0 top-0 z-50 transition-colors duration-300",
        scrolled
          ? "backdrop-blur-md bg-[#070A16]/70 border-b border-white/10"
          : "bg-transparent"
      )}
    >
      <a
        href={TICKETS.url}
        data-testid="ticket-banner"
        className="group flex h-9 items-center justify-center gap-2 bg-brand-gradient px-3 text-center text-[11px] font-medium text-white sm:gap-3 sm:text-xs"
      >
        <Ticket className="hidden h-3.5 w-3.5 shrink-0 sm:block" />
        <span className="shrink-0 rounded-full bg-white/20 px-2 py-0.5 font-mono-accent text-[9px] font-semibold tracking-[0.2em]">
          {TICKETS.bannerBadge}
        </span>
        <span className="hidden truncate sm:inline">{TICKETS.bannerText}</span>
        <span className="truncate sm:hidden">{TICKETS.bannerShort}</span>
        <ArrowRight className="h-3.5 w-3.5 shrink-0 sm:hidden" />
        <span className="hidden shrink-0 items-center gap-1 font-semibold underline-offset-4 group-hover:underline sm:inline-flex">
          {TICKETS.bannerCta}
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
        </span>
      </a>
      <nav className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          data-testid="navbar-logo-link"
          className="flex items-center gap-2"
        >
          <PlanetLogo />
          <div className="leading-none">
            <span className="font-display text-sm font-bold tracking-[0.14em] text-white">
              GALAXY
            </span>{" "}
            <span className="font-display text-sm font-bold tracking-[0.14em] text-gl-blue2">
              LEAGUE
            </span>
          </div>
        </Link>

        <div className="hidden items-center gap-7 md:flex">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.href}
              data-testid={`navbar-link-${l.label.toLowerCase()}`}
              className="group relative text-xs tracking-[0.18em] text-white/65 transition-colors hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-brand-gradient transition-all duration-300 group-hover:w-full" />
            </Link>
          ))}
          <a
            href={TICKETS.url}
            data-testid="navbar-tickets-button"
            className="rounded-full bg-brand-gradient px-5 py-2 text-xs font-semibold tracking-wide text-white glow-sm transition-[filter,transform] duration-200 hover:brightness-110 active:scale-[0.98]"
          >
            {TICKETS.cta}
          </a>
        </div>

        {/* Mobile */}
        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <button
                type="button"
                data-testid="navbar-mobile-menu-button"
                aria-label="Menü öffnen"
                className="rounded-lg border border-white/10 bg-white/5 p-2 text-white/80 backdrop-blur transition-colors hover:border-gl-blue/40 hover:text-white"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              data-testid="navbar-mobile-sheet"
              className="w-[88%] max-w-sm border-l border-white/10 bg-[#070A16]/95 p-0 text-white backdrop-blur-xl"
            >
              {/* ambient glow */}
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(420px_circle_at_85%_-5%,rgba(58,123,255,0.20),transparent_60%)]" />

              <div className="relative flex h-full flex-col">
                {/* Header */}
                <div className="flex items-center gap-2 border-b border-white/10 px-6 py-5">
                  <PlanetLogo />
                  <div className="leading-none">
                    <span className="font-display text-sm font-bold tracking-[0.14em] text-white">
                      GALAXY
                    </span>{" "}
                    <span className="font-display text-sm font-bold tracking-[0.14em] text-gl-blue2">
                      LEAGUE
                    </span>
                    <div className="mt-1 font-mono-accent text-[9px] uppercase tracking-[0.28em] text-white/40">
                      Sept 2026 · Wien
                    </div>
                  </div>
                </div>

                {/* Nav links */}
                <nav className="flex flex-1 flex-col gap-1.5 px-4 py-6">
                  {NAV_LINKS.map((l) => (
                    <SheetClose asChild key={l.label}>
                      <Link
                        to={l.href}
                        data-testid={`navbar-mobile-link-${l.label.toLowerCase()}`}
                        className="group flex items-center justify-between rounded-xl border border-transparent px-4 py-3.5 text-sm tracking-[0.16em] text-white/80 transition-colors duration-200 hover:border-white/10 hover:bg-white/5 hover:text-white"
                      >
                        {l.label}
                        <ChevronRight className="h-4 w-4 text-white/30 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-gl-blue2" />
                      </Link>
                    </SheetClose>
                  ))}
                </nav>

                {/* Bottom — CTA + social */}
                <div className="border-t border-white/10 px-6 py-6">
                  <SheetClose asChild>
                    <a
                      href={TICKETS.url}
                      data-testid="navbar-mobile-tickets-button"
                      className="group flex w-full items-center justify-center gap-2 rounded-full bg-brand-gradient px-5 py-3.5 text-xs font-semibold tracking-wide text-white glow-sm transition-[filter,transform] duration-200 hover:brightness-110 active:scale-[0.98]"
                    >
                      {TICKETS.cta}
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </a>
                  </SheetClose>

                  <div className="mt-5 flex items-center justify-between">
                    <span className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-white/40">
                      1 Day. 1 Champion.
                    </span>
                    {INSTAGRAM && (
                      <a
                        href={INSTAGRAM}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                        data-testid="navbar-mobile-instagram-link"
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-white/65 transition-colors duration-200 hover:border-gl-blue/50 hover:text-white"
                      >
                        <Instagram className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
